
import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import { Tablet, Monitor, Smartphone, ArrowRight, CheckCircle } from "lucide-react";

const Products = () => {
  return (
    <Layout>
      <Helmet>
        <title>All Products - Custom Tablets, PCs & Smart Gadgets | TrueCore Gadgets India</title>
        <meta name="description" content="Explore TrueCore Gadgets' complete range of custom electronics: Android tablets, business PCs, smart gadgets, and enterprise solutions. Made in India with 25+ years expertise." />
        <meta name="keywords" content="TrueCore products, custom tablets India, business PCs, smart gadgets, enterprise electronics, Android devices, custom manufacturing" />
        
        <link rel="canonical" href="https://www.truecoregadgets.com/products" />
        
        <meta property="og:type" content="website" />
        <meta property="og:title" content="All Products - Custom Electronics | TrueCore Gadgets" />
        <meta property="og:description" content="Complete range of custom tablets, PCs, and smart gadgets manufactured in India with 25+ years of expertise." />
        <meta property="og:url" content="https://www.truecoregadgets.com/products" />
        <meta property="og:image" content="https://www.truecoregadgets.com/lovable-uploads/461a823a-5781-4980-ad39-ffec726a88a3.png" />
      </Helmet>

      <div className="container mx-auto py-12 px-4">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold gradient-text mb-6">Our Products</h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Discover our comprehensive range of custom electronics designed for businesses and professionals across India
          </p>
        </div>

        {/* Product Categories */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          <Link to="/products/tablets" className="group">
            <div className="bg-card p-8 rounded-lg shadow-md border hover:shadow-lg transition-all duration-300 group-hover:border-primary/50">
              <div className="text-center">
                <div className="bg-primary/10 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6 group-hover:bg-primary/20 transition-colors">
                  <Tablet className="h-10 w-10 text-primary" />
                </div>
                <h2 className="text-2xl font-bold mb-4">Custom Tablets</h2>
                <p className="text-muted-foreground mb-6">
                  Ruggedized Android tablets designed for enterprise use, field operations, and specialized applications.
                </p>
                <div className="flex items-center justify-center text-primary group-hover:text-primary/80">
                  <span className="mr-2">Explore Tablets</span>
                  <ArrowRight className="h-4 w-4" />
                </div>
              </div>
            </div>
          </Link>

          <Link to="/products/pcs" className="group">
            <div className="bg-card p-8 rounded-lg shadow-md border hover:shadow-lg transition-all duration-300 group-hover:border-primary/50">
              <div className="text-center">
                <div className="bg-accent/10 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6 group-hover:bg-accent/20 transition-colors">
                  <Monitor className="h-10 w-10 text-accent" />
                </div>
                <h2 className="text-2xl font-bold mb-4">Custom PCs</h2>
                <p className="text-muted-foreground mb-6">
                  High-performance workstations and business computers tailored for specific industry requirements.
                </p>
                <div className="flex items-center justify-center text-accent group-hover:text-accent/80">
                  <span className="mr-2">Explore PCs</span>
                  <ArrowRight className="h-4 w-4" />
                </div>
              </div>
            </div>
          </Link>

          <Link to="/products/smart-gadgets" className="group">
            <div className="bg-card p-8 rounded-lg shadow-md border hover:shadow-lg transition-all duration-300 group-hover:border-primary/50">
              <div className="text-center">
                <div className="bg-secondary/10 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6 group-hover:bg-secondary/20 transition-colors">
                  <Smartphone className="h-10 w-10 text-secondary" />
                </div>
                <h2 className="text-2xl font-bold mb-4">Smart Gadgets</h2>
                <p className="text-muted-foreground mb-6">
                  IoT devices, embedded systems, and specialized electronics for modern business applications.
                </p>
                <div className="flex items-center justify-center text-secondary group-hover:text-secondary/80">
                  <span className="mr-2">Explore Gadgets</span>
                  <ArrowRight className="h-4 w-4" />
                </div>
              </div>
            </div>
          </Link>
        </div>

        {/* Why Choose Our Products */}
        <div className="bg-muted/50 rounded-lg p-8 mb-16">
          <h2 className="text-3xl font-bold text-center mb-12">Why Choose TrueCore Products?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <CheckCircle className="h-12 w-12 text-primary mx-auto mb-4" />
              <h3 className="font-bold mb-2">25+ Years Experience</h3>
              <p className="text-sm text-muted-foreground">Proven track record in electronics manufacturing</p>
            </div>
            <div className="text-center">
              <CheckCircle className="h-12 w-12 text-primary mx-auto mb-4" />
              <h3 className="font-bold mb-2">Custom Solutions</h3>
              <p className="text-sm text-muted-foreground">Tailored to your specific business requirements</p>
            </div>
            <div className="text-center">
              <CheckCircle className="h-12 w-12 text-primary mx-auto mb-4" />
              <h3 className="font-bold mb-2">Quality Assured</h3>
              <p className="text-sm text-muted-foreground">ISO certified manufacturing processes</p>
            </div>
            <div className="text-center">
              <CheckCircle className="h-12 w-12 text-primary mx-auto mb-4" />
              <h3 className="font-bold mb-2">Pan-India Support</h3>
              <p className="text-sm text-muted-foreground">Nationwide service and support network</p>
            </div>
          </div>
        </div>

        {/* Industry Applications */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-12">Industry Applications</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-card p-6 rounded-lg shadow-md border">
              <h3 className="text-xl font-bold mb-4">Healthcare</h3>
              <p className="text-muted-foreground">Patient monitoring systems, medical tablets, and diagnostic equipment</p>
            </div>
            <div className="bg-card p-6 rounded-lg shadow-md border">
              <h3 className="text-xl font-bold mb-4">Retail & POS</h3>
              <p className="text-muted-foreground">Point-of-sale systems, inventory management, and customer service terminals</p>
            </div>
            <div className="bg-card p-6 rounded-lg shadow-md border">
              <h3 className="text-xl font-bold mb-4">Education</h3>
              <p className="text-muted-foreground">Interactive learning tablets, classroom PCs, and educational technology solutions</p>
            </div>
            <div className="bg-card p-6 rounded-lg shadow-md border">
              <h3 className="text-xl font-bold mb-4">Manufacturing</h3>
              <p className="text-muted-foreground">Industrial control systems, production monitoring, and automation devices</p>
            </div>
            <div className="bg-card p-6 rounded-lg shadow-md border">
              <h3 className="text-xl font-bold mb-4">Logistics</h3>
              <p className="text-muted-foreground">Warehouse management systems, tracking devices, and mobile terminals</p>
            </div>
            <div className="bg-card p-6 rounded-lg shadow-md border">
              <h3 className="text-xl font-bold mb-4">Government</h3>
              <p className="text-muted-foreground">Secure computing solutions, citizen service kiosks, and administrative systems</p>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="bg-primary/10 rounded-lg p-8 text-center">
          <h2 className="text-2xl font-bold mb-4">Need a Custom Solution?</h2>
          <p className="text-muted-foreground mb-6">
            Can't find exactly what you're looking for? Our team specializes in creating custom electronics solutions.
          </p>
          <div className="space-x-4">
            <Link to="/contact-truecore" className="tech-btn-primary">
              Get Custom Quote
            </Link>
            <Link to="/technical-specifications" className="tech-btn-outline">
              View Specifications
            </Link>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Products;
